import { useState } from 'react';
import { useContent, saveContentSection, saveTargets } from '../hooks/useContent';

const TABS = [
  { id: 'company',    label: '🏢 Company Info' },
  { id: 'pricing',    label: '💰 Pricing Tiers' },
  { id: 'templates',  label: '✉️ Email Templates' },
  { id: 'scripts',    label: '📞 Call Scripts' },
  { id: 'objections', label: '🛡️ Objections' },
  { id: 'icp',        label: '🎯 ICP & Clients' },
  { id: 'targets',    label: '📊 KPI Targets' }
];

export default function ContentEditor() {
  const { content, targets, loading, reload } = useContent();
  const [activeTab, setActiveTab] = useState('company');
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);
  const [error, setError]         = useState('');

  async function handleSave(key, value) {
    setSaving(true); setSaved(false); setError('');
    try {
      if (key === 'targets') await saveTargets(value);
      else await saveContentSection(key, value);
      await reload();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message || 'Save failed.');
    } finally { setSaving(false); }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-500 text-sm">
        Loading content…
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-full">

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Page title + save status */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Content Management</h1>
            <p className="text-xs text-slate-500 mt-0.5">Changes go live immediately on the CRM — no redeploy needed.</p>
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-xs text-slate-500">Saving…</span>}
            {saved  && <span className="text-xs text-emerald-600 font-medium">✓ Saved successfully</span>}
            {error  && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 overflow-x-auto mb-6 border-b border-slate-200 pb-0">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition -mb-px ${
                activeTab === t.id
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Panels */}
        {activeTab === 'company'    && <CompanyEditor    data={content.company}          onSave={v => handleSave('company', v)}         saving={saving} />}
        {activeTab === 'pricing'    && <PricingEditor    data={content.pricing_tiers}    onSave={v => handleSave('pricing_tiers', v)}   saving={saving} />}
        {activeTab === 'templates'  && <TemplatesEditor  data={content.email_templates}  onSave={v => handleSave('email_templates', v)} saving={saving} />}
        {activeTab === 'scripts'    && <ScriptsEditor    data={content.call_scripts}     onSave={v => handleSave('call_scripts', v)}    saving={saving} />}
        {activeTab === 'objections' && <ObjectionsEditor data={content.objections}       onSave={v => handleSave('objections', v)}      saving={saving} />}
        {activeTab === 'icp'        && <IcpEditor        icp={content.icp} trusted={content.trusted_by} onSave={handleSave} saving={saving} />}
        {activeTab === 'targets'    && <TargetsEditor    data={targets}                  onSave={v => handleSave('targets', v)}         saving={saving} />}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Shared layout
--------------------------------------------------------------------------- */
function EditorLayout({ editor, preview }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div>{editor}</div>
      <div>
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Live Preview</div>
        <div className="card overflow-auto max-h-[72vh] p-5 text-sm">
          {preview}
        </div>
      </div>
    </div>
  );
}

function SaveBtn({ saving, onClick, label = 'Save changes' }) {
  return (
    <button onClick={onClick} disabled={saving} className="btn-primary mt-5">
      {saving ? 'Saving…' : label}
    </button>
  );
}

/* ---------------------------------------------------------------------------
   Company Info
--------------------------------------------------------------------------- */
function CompanyEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data || {});
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const FIELDS = [
    ['name',            'Company name',             false],
    ['shortName',       'Short name',               false],
    ['slogan',          'Slogan',                   true],
    ['positioning',     'Positioning statement',    true],
    ['address',         'Address',                  false],
    ['phone',           'Phone',                    false],
    ['email',           'Email',                    false],
    ['website',         'Website URL',              false],
    ['yearsExperience', 'Years experience label',   false],
    ['cta',             'Free audit CTA text',      true]
  ];

  return (
    <EditorLayout
      editor={
        <div className="card p-5 space-y-3">
          {FIELDS.map(([key, label, multiline]) => (
            <div key={key}>
              <label className="label">{label}</label>
              {multiline
                ? <textarea className="input h-20 resize-none" value={form[key] || ''} onChange={e => set(key, e.target.value)} />
                : <input className="input" value={form[key] || ''} onChange={e => set(key, e.target.value)} />
              }
            </div>
          ))}
          <SaveBtn saving={saving} onClick={() => onSave(form)} />
        </div>
      }
      preview={
        <div className="space-y-3 text-slate-700">
          <div className="font-bold text-brand-700 text-base">{form.name}</div>
          <div className="text-xs text-slate-500 italic">{form.slogan}</div>
          <div className="text-xs leading-relaxed">{form.positioning}</div>
          <div className="border-t pt-3 text-xs space-y-1">
            <div>📍 {form.address}</div>
            <div>📞 {form.phone}</div>
            <div>✉️ {form.email}</div>
            <div>🌐 {form.website}</div>
          </div>
          <div className="bg-brand-50 border border-brand-100 rounded-lg p-3 text-xs text-brand-800">
            <strong>CTA:</strong> {form.cta}
          </div>
        </div>
      }
    />
  );
}

/* ---------------------------------------------------------------------------
   Pricing Tiers
--------------------------------------------------------------------------- */
function PricingEditor({ data, onSave, saving }) {
  const [tiers, setTiers] = useState(data || []);

  const setTier   = (i, k, v) => setTiers(ts => ts.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const setBullet = (ti, bi, v) => setTiers(ts => ts.map((t, i) => {
    if (i !== ti) return t;
    const b = [...t.bullets]; b[bi] = v; return { ...t, bullets: b };
  }));
  const addBullet    = ti => setTiers(ts => ts.map((t, i) => i === ti ? { ...t, bullets: [...t.bullets, ''] } : t));
  const removeBullet = (ti, bi) => setTiers(ts => ts.map((t, i) => i === ti ? { ...t, bullets: t.bullets.filter((_, j) => j !== bi) } : t));
  const KES = n => 'KES ' + Number(n).toLocaleString('en-KE');

  return (
    <EditorLayout
      editor={
        <div className="space-y-4">
          {tiers.map((tier, i) => (
            <div key={tier.name} className="card p-5 space-y-3">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-brand-700">{tier.name}</h3>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={!!tier.recommended} onChange={e => setTier(i, 'recommended', e.target.checked)} />
                  Mark as recommended
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Monthly price (KES)</label>
                  <input type="number" className="input" value={tier.monthlyKES} onChange={e => setTier(i, 'monthlyKES', Number(e.target.value))} />
                </div>
                <div>
                  <label className="label">Price label override</label>
                  <input className="input" placeholder="e.g. KES 200,000+" value={tier.monthlyKESLabel || ''} onChange={e => setTier(i, 'monthlyKESLabel', e.target.value || null)} />
                </div>
              </div>
              <div>
                <label className="label">Headline</label>
                <input className="input" value={tier.headline} onChange={e => setTier(i, 'headline', e.target.value)} />
              </div>
              <div>
                <label className="label">Best for</label>
                <input className="input" value={tier.bestFor} onChange={e => setTier(i, 'bestFor', e.target.value)} />
              </div>
              <div>
                <label className="label">Bullet points</label>
                <div className="space-y-1.5">
                  {tier.bullets.map((b, bi) => (
                    <div key={bi} className="flex gap-2">
                      <input className="input flex-1" value={b} onChange={e => setBullet(i, bi, e.target.value)} />
                      <button onClick={() => removeBullet(i, bi)} className="text-red-400 hover:text-red-600 px-2 text-sm">✕</button>
                    </div>
                  ))}
                  <button onClick={() => addBullet(i)} className="text-xs text-brand-600 hover:underline">+ Add bullet</button>
                </div>
              </div>
            </div>
          ))}
          <SaveBtn saving={saving} onClick={() => onSave(tiers)} />
        </div>
      }
      preview={
        <div className="space-y-3">
          {tiers.map(t => (
            <div key={t.name} className={`border rounded-xl p-4 ${t.recommended ? 'border-brand-500 ring-1 ring-brand-500' : 'border-slate-200'}`}>
              {t.recommended && <span className="text-xs bg-brand-500 text-white rounded-full px-2 py-0.5 mb-2 inline-block">Recommended</span>}
              <div className="font-semibold text-brand-700 text-sm">{t.name}</div>
              <div className="text-xl font-bold">{t.monthlyKESLabel || KES(t.monthlyKES)}<span className="text-xs font-normal text-slate-500"> /mo</span></div>
              <div className="text-xs text-slate-500 mb-2">{t.headline}</div>
              <ul className="space-y-1">
                {t.bullets.map((b, i) => <li key={i} className="flex gap-1.5 text-xs"><span className="text-brand-500 font-bold">✓</span>{b}</li>)}
              </ul>
              <div className="text-xs text-slate-500 mt-3 pt-2 border-t"><strong>Best for:</strong> {t.bestFor}</div>
            </div>
          ))}
        </div>
      }
    />
  );
}

/* ---------------------------------------------------------------------------
   Generic template list editor (email templates + call scripts)
--------------------------------------------------------------------------- */
function TemplateListEditor({ data, onSave, saving, hasSubject }) {
  const [items, setItems]     = useState(data || []);
  const [activeIdx, setIdx]   = useState(0);

  const setField = (k, v) => setItems(ts => ts.map((t, i) => i === activeIdx ? { ...t, [k]: v } : t));

  function addItem() {
    const item = { id: `item-${Date.now()}`, name: 'New item', body: '' };
    if (hasSubject) item.subject = '';
    setItems(ts => [...ts, item]);
    setIdx(items.length);
  }

  function removeItem(idx) {
    const next = items.filter((_, i) => i !== idx);
    setItems(next);
    setIdx(Math.min(activeIdx, next.length - 1));
  }

  const active = items[activeIdx];

  return (
    <EditorLayout
      editor={
        <div className="card p-5">
          {/* Sidebar list */}
          <div className="flex gap-2 flex-wrap mb-4 pb-4 border-b border-slate-100">
            {items.map((t, idx) => (
              <div key={t.id} className="flex items-center gap-1">
                <button
                  onClick={() => setIdx(idx)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition ${
                    idx === activeIdx
                      ? 'bg-brand-700 text-white border-brand-700'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-brand-400'
                  }`}
                >
                  {t.name}
                </button>
                {items.length > 1 && (
                  <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 text-xs font-bold">✕</button>
                )}
              </div>
            ))}
            <button onClick={addItem} className="text-xs text-brand-600 hover:underline px-2">+ Add</button>
          </div>

          {active && (
            <div className="space-y-3">
              <div>
                <label className="label">Name (shown in sidebar)</label>
                <input className="input" value={active.name} onChange={e => setField('name', e.target.value)} />
              </div>
              {hasSubject && (
                <div>
                  <label className="label">Subject line</label>
                  <input className="input" value={active.subject || ''} onChange={e => setField('subject', e.target.value)} placeholder="Use {{variable}} for dynamic fields" />
                </div>
              )}
              <div>
                <label className="label">Body</label>
                <textarea
                  className="input font-mono text-xs leading-relaxed h-72 resize-y"
                  value={active.body}
                  onChange={e => setField('body', e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">Use {'{{variable_name}}'} for tokens that users fill in on the CRM.</p>
              </div>
            </div>
          )}
          <SaveBtn saving={saving} onClick={() => onSave(items)} />
        </div>
      }
      preview={
        active ? (
          <div className="space-y-2">
            <div className="font-semibold text-slate-800">{active.name}</div>
            {hasSubject && active.subject && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs font-medium">
                Subject: {active.subject}
              </div>
            )}
            <pre className="whitespace-pre-wrap font-sans text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 leading-relaxed">
              {active.body}
            </pre>
          </div>
        ) : <div className="text-slate-400 text-xs">Select a template.</div>
      }
    />
  );
}

function TemplatesEditor({ data, onSave, saving }) {
  return <TemplateListEditor data={data} onSave={onSave} saving={saving} hasSubject />;
}
function ScriptsEditor({ data, onSave, saving }) {
  return <TemplateListEditor data={data} onSave={onSave} saving={saving} hasSubject={false} />;
}

/* ---------------------------------------------------------------------------
   Objections
--------------------------------------------------------------------------- */
function ObjectionsEditor({ data, onSave, saving }) {
  const [items, setItems] = useState(data || []);
  const set = (i, k, v) => setItems(ts => ts.map((t, idx) => idx === i ? { ...t, [k]: v } : t));

  return (
    <EditorLayout
      editor={
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="card p-4 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-400">Objection {i + 1}</span>
                {items.length > 1 && (
                  <button onClick={() => setItems(ts => ts.filter((_, j) => j !== i))} className="text-xs text-red-400 hover:text-red-600">Remove</button>
                )}
              </div>
              <div>
                <label className="label">Objection</label>
                <input className="input" value={item.objection} onChange={e => set(i, 'objection', e.target.value)} />
              </div>
              <div>
                <label className="label">Response</label>
                <textarea className="input h-24 resize-none" value={item.response} onChange={e => set(i, 'response', e.target.value)} />
              </div>
            </div>
          ))}
          <button onClick={() => setItems(ts => [...ts, { objection: '', response: '' }])} className="text-xs text-brand-600 hover:underline">+ Add objection</button>
          <SaveBtn saving={saving} onClick={() => onSave(items)} />
        </div>
      }
      preview={
        <div className="space-y-2">
          {items.map((o, i) => (
            <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 font-semibold text-xs">{o.objection || '(empty)'}</div>
              <div className="px-3 py-2 text-xs text-slate-600 leading-relaxed">{o.response || '(no response)'}</div>
            </div>
          ))}
        </div>
      }
    />
  );
}

/* ---------------------------------------------------------------------------
   ICP & Trusted By
--------------------------------------------------------------------------- */
function IcpEditor({ icp = [], trusted = [], onSave, saving }) {
  const [icpItems, setIcp]         = useState(icp);
  const [trustedItems, setTrusted] = useState(trusted);

  const setIcpField     = (i, k, v) => setIcp(ts => ts.map((t, idx) => idx === i ? { ...t, [k]: v } : t));
  const setTrustedItem  = (i, v)    => setTrusted(ts => ts.map((t, idx) => idx === i ? v : t));

  async function saveAll() {
    await onSave('icp', icpItems);
    await onSave('trusted_by', trustedItems);
  }

  return (
    <EditorLayout
      editor={
        <div className="space-y-5">
          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-slate-800">Ideal Client Profile</h3>
            {icpItems.map((item, i) => (
              <div key={i} className="grid grid-cols-2 gap-2 items-start">
                <div>
                  <label className="label">Segment</label>
                  <input className="input" value={item.segment} onChange={e => setIcpField(i, 'segment', e.target.value)} />
                </div>
                <div>
                  <label className="label">Hook / why us</label>
                  <input className="input" value={item.hook} onChange={e => setIcpField(i, 'hook', e.target.value)} />
                </div>
              </div>
            ))}
            <button onClick={() => setIcp(ts => [...ts, { segment: '', hook: '' }])} className="text-xs text-brand-600 hover:underline">+ Add segment</button>
          </div>

          <div className="card p-5 space-y-3">
            <h3 className="font-semibold text-slate-800">Trusted By</h3>
            {trustedItems.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input className="input flex-1" value={c} onChange={e => setTrustedItem(i, e.target.value)} />
                <button onClick={() => setTrusted(ts => ts.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 px-2 text-sm">✕</button>
              </div>
            ))}
            <button onClick={() => setTrusted(ts => [...ts, ''])} className="text-xs text-brand-600 hover:underline">+ Add client</button>
          </div>

          <SaveBtn saving={saving} onClick={saveAll} />
        </div>
      }
      preview={
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase mb-2">ICP Segments</div>
            <div className="space-y-2">
              {icpItems.map((item, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3">
                  <div className="font-semibold text-xs">{item.segment || '(empty)'}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{item.hook}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase mb-2">Trusted By</div>
            <div className="flex flex-wrap gap-1.5">
              {trustedItems.map((c, i) => (
                <span key={i} className="bg-brand-50 text-brand-700 border border-brand-100 rounded-full px-3 py-1 text-xs">{c}</span>
              ))}
            </div>
          </div>
        </div>
      }
    />
  );
}

/* ---------------------------------------------------------------------------
   KPI Targets
--------------------------------------------------------------------------- */
const TARGET_LABELS = {
  newProspectsEnriched:    'New Prospects Enriched / week',
  outreachEmailsSent:      'Outreach Emails Sent / week',
  discoveryCallsCompleted: 'Discovery Calls Completed / week',
  qualifiedLeads:          'Qualified Leads / week',
  founderPresentations:    'Founder Presentations / week',
  proposalsSent:           'Proposals Sent / week',
  retainersClosed:         'Retainers Closed / week'
};

function TargetsEditor({ data, onSave, saving }) {
  const [form, setForm] = useState(data || {});

  return (
    <EditorLayout
      editor={
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold text-slate-800">Weekly KPI Targets</h2>
          <p className="text-xs text-slate-500">These appear on the Dashboard scorecard and weekly Reports in the CRM.</p>
          {Object.entries(TARGET_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <label className="text-sm text-slate-700">{label}</label>
              <input
                type="number" min="0"
                className="input w-24 text-center"
                value={form[key] ?? 0}
                onChange={e => setForm(f => ({ ...f, [key]: Number(e.target.value) }))}
              />
            </div>
          ))}
          <SaveBtn saving={saving} onClick={() => onSave(form)} />
        </div>
      }
      preview={
        <div className="space-y-1">
          <div className="text-xs font-semibold text-slate-400 uppercase mb-3">Current targets</div>
          {Object.entries(TARGET_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-xs text-slate-600">{label}</span>
              <span className="font-bold text-brand-700">{form[key] ?? 0}</span>
            </div>
          ))}
        </div>
      }
    />
  );
}
